/**
 * Roving focus for automatic-activation tablists — and, since BC-11, radio
 * groups (`selector: '[role="radio"]'`): the current item is the one marked
 * `aria-selected` OR `aria-checked`. The host owns selection.
 */
export function attachTablistNav(
  container,
  { selector = '[role="tab"]', orientation = 'horizontal', onActivate } = {}
) {
  const tabs = () =>
    [...container.querySelectorAll(selector)].filter(
      (tab) =>
        !tab.disabled &&
        tab.getAttribute('aria-disabled') !== 'true' &&
        !tab.closest('[hidden], [inert]')
    );
  function sync() {
    const all = [...container.querySelectorAll(selector)];
    const enabled = tabs();
    const active =
      enabled.find(
        (tab) =>
          tab.getAttribute('aria-selected') === 'true' ||
          tab.getAttribute('aria-checked') === 'true'
      ) || enabled[0];
    for (const tab of all) tab.tabIndex = tab === active ? 0 : -1;
  }
  function keydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const current = event.target.closest(selector);
    if (!current || !container.contains(current)) return;
    const enabled = tabs();
    const index = enabled.indexOf(current);
    if (index < 0) return;
    const previous = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
    const next = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    let target;
    if (event.key === 'Home') target = enabled[0];
    else if (event.key === 'End') target = enabled.at(-1);
    else if (event.key === previous)
      target = enabled[(index + enabled.length - 1) % enabled.length];
    else if (event.key === next) target = enabled[(index + 1) % enabled.length];
    else return;
    event.preventDefault();
    onActivate?.(target);
    sync();
    target.focus();
  }
  container.setAttribute('aria-orientation', orientation);
  container.addEventListener('keydown', keydown);
  sync();
  return { sync, destroy: () => container.removeEventListener('keydown', keydown) };
}
